/**
 * Aritmética de fechas civiles (sin zona horaria) sobre cadenas AAAA-MM-DD.
 * Se trabaja con "número de día" (días desde 1970-01-01) para evitar
 * cualquier dependencia de la zona horaria del entorno de ejecución.
 */
export type LocalDate = string;

const MS_PER_DAY = 86_400_000;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseLocalDate(d: LocalDate): { y: number; m: number; d: number } {
  const match = DATE_RE.exec(d);
  if (!match) throw new Error(`Fecha civil no válida: ${d}`);
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
}

export function isValidLocalDate(d: unknown): d is LocalDate {
  if (typeof d !== 'string' || !DATE_RE.test(d)) return false;
  const { y, m, d: day } = parseLocalDate(d);
  if (y < 1900 || y > 2999 || m < 1 || m > 12 || day < 1) return false;
  return day <= daysInMonth(y, m);
}

export function formatLocalDate(y: number, m: number, d: number): LocalDate {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export function toDayNumber(d: LocalDate): number {
  const { y, m, d: day } = parseLocalDate(d);
  return Math.round(Date.UTC(y, m - 1, day) / MS_PER_DAY);
}

export function fromDayNumber(n: number): LocalDate {
  const dt = new Date(n * MS_PER_DAY);
  return formatLocalDate(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

export function addDays(d: LocalDate, n: number): LocalDate {
  return fromDayNumber(toDayNumber(d) + n);
}

/** Días desde `a` hasta `b` (b − a). */
export function diffDays(a: LocalDate, b: LocalDate): number {
  return toDayNumber(b) - toDayNumber(a);
}

/** Día ISO de la semana: 1 = lunes … 7 = domingo. */
export function isoWeekday(d: LocalDate): number {
  // 1970-01-01 fue jueves (4).
  const n = toDayNumber(d);
  return ((((n + 3) % 7) + 7) % 7) + 1;
}

export function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

export function startOfMonth(d: LocalDate): LocalDate {
  const { y, m } = parseLocalDate(d);
  return formatLocalDate(y, m, 1);
}

export function endOfMonth(d: LocalDate): LocalDate {
  const { y, m } = parseLocalDate(d);
  return formatLocalDate(y, m, daysInMonth(y, m));
}

export function addMonths(d: LocalDate, n: number): LocalDate {
  const { y, m } = parseLocalDate(d);
  const total = y * 12 + (m - 1) + n;
  return formatLocalDate(Math.floor(total / 12), (total % 12) + 1, 1);
}

export function monthKey(d: LocalDate): string {
  return d.slice(0, 7);
}

/** Primer día >= d cuyo día ISO de la semana es `weekday`. */
export function alignForward(d: LocalDate, weekday: number): LocalDate {
  const delta = (weekday - isoWeekday(d) + 7) % 7;
  return addDays(d, delta);
}

/** Último día <= d cuyo día ISO de la semana es `weekday`. */
export function alignBack(d: LocalDate, weekday: number): LocalDate {
  const delta = (isoWeekday(d) - weekday + 7) % 7;
  return addDays(d, -delta);
}

export function minDate(a: LocalDate, b: LocalDate): LocalDate {
  return a <= b ? a : b;
}

export function maxDate(a: LocalDate, b: LocalDate): LocalDate {
  return a >= b ? a : b;
}

export function* eachDay(from: LocalDate, to: LocalDate): Generator<LocalDate> {
  const end = toDayNumber(to);
  for (let n = toDayNumber(from); n <= end; n++) yield fromDayNumber(n);
}
